import * as THREE from 'three/webgpu';

import { CREATURE as CREATURE_BAKE } from '../bake/BakeConfig.js';
import { OccluderSet } from '../bake/Occluders.js';
import { applyBake, bakeSync } from '../bake/bakeVertexLight.js';
import { GeoBuilder } from '../build/GeoBuilder.js';
import { box, icoRock } from '../build/prims.js';
import { mergeNonIndexed } from '../build/mergeGeo.js';
import { configureSpriteTexture } from '../assets/playerSprite.js';
import { streamRng } from '../math/rng.js';
import { rigFromGeometry } from './creatures.js';

/**
 * @file Enemy TYPES: the bridge between "what a monster is" and the two
 * renderers that draw it.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * REAL ENEMIES ARE AUTHORED, NOT GENERATED
 * ─────────────────────────────────────────────────────────────────────────────
 * The procedural creature system that used to live here was scrapped: randomised
 * primitive assembly cannot produce an intentional silhouette, and no parameter
 * tuning fixes that. Enemies now come from files — see `actors/enemyArt.js`
 * (`loadEnemyType`) and the roster in `src/game/enemies/`.
 *
 * What remains here is:
 *  • {@link defineEnemyType} — the record every consumer reads;
 *  • {@link rowFor} — the yaw quantiser both renderers share;
 *  • {@link buildPlaceholderType} — ONE deliberately-plain grey lump so a level
 *    with no art still runs and the AI/HUD stay testable. It is a placeholder,
 *    it is named `placeholder`, and it is not a style to imitate.
 *
 * @module engine/actors/enemyTypes
 */

/** @typedef {import('../types.js').EnemyType} EnemyType */

/** The placeholder's atlas layout: one frame, one facing. Nothing to see. */
const ATLAS = Object.freeze({ cols: 1, rows: 1 });

/** Row order for a 4-facing atlas — the convention {@link rowFor} quantises to. */
export const FACING_ROWS = Object.freeze(['front', 'right', 'back', 'left']);

const TAU = Math.PI * 2;

// ─────────────────────────────────────────────────────────────────────────────
// The type record
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Normalises a type description into the {@link EnemyType} shape the manager,
 * the spawner and both renderers read. Missing gameplay fields get the plan's
 * defaults so a level can declare `{id, radius, speed, hp}` and be done.
 *
 * @param {Object} o
 * @param {string} o.id
 * @param {string} [o.name]
 * @param {number} [o.radius=0.45] Collision radius, metres.
 * @param {number} [o.speed=3.2] Chase speed, m/s.
 * @param {number} [o.hp=30]
 * @param {number} [o.worldHeight=1.4] Visual height, metres — SHARED by both
 *   backends, so re-authoring a creature from a PNG to a model keeps its size.
 * @param {number} [o.damage=8]
 * @param {number} [o.attackRange]
 * @param {number} [o.attackCooldown=1.1] Seconds (informational; the FSM's
 *   `windup + recover` is the live number).
 * @param {number} [o.aggroRange=0] 0 = use the manager's global band.
 * @param {import('../types.js').FireSpec|null} [o.fire] TEAM/FACTION TARGETING
 *   ONLY: makes this a RANGED type — it holds at `range` and shoots instead of
 *   closing to contact. Normalised to `null` unless all three numbers are
 *   positive, because a half-authored `fire` block (a range with no interval)
 *   would otherwise turn a melee creature into a statue that never fires. Hero
 *   mode never reads it, so adding `fire` to a def cannot change that path.
 * @param {import('../types.js').EnemyRigBackend|null} [o.rig]
 * @param {import('../types.js').EnemySpriteBackend|null} [o.sprite]
 * @param {number} [o.blobR] Contact-shadow radius; defaults to `radius * 1.25`.
 * @param {[number,number,number]} [o.tint]
 * @param {import('../types.js').EnemyDef} [o.def] Source definition, kept for
 *   tooling and hot-reload.
 * @returns {EnemyType}
 */
export function defineEnemyType(o) {
  if (!o?.id) throw new Error('defineEnemyType: `id` is required');
  const radius = o.radius ?? 0.45;
  const worldHeight = o.worldHeight ?? o.height ?? 1.4;
  return {
    id: o.id,
    name: o.name ?? o.id,
    radius,
    speed: o.speed ?? 3.2,
    hp: o.hp ?? 30,
    damage: o.damage ?? 8,
    attackRange: o.attackRange ?? radius + 0.6,
    attackCooldown: o.attackCooldown ?? 1.1,
    aggroRange: o.aggroRange ?? 0,
    height: worldHeight,
    /** Alias kept because both renderers and the SpriteBatch speak this name. */
    worldHeight,
    blobR: o.blobR ?? radius * 1.25,
    fire: normalizeFire(o.fire),
    tint: o.tint ?? [1, 1, 1],
    rig: o.rig ?? null,
    sprite: o.sprite ?? null,
    def: o.def,
    /** Frees both backends. Idempotent. */
    dispose() {
      this.rig?.dispose?.();
      this.sprite?.atlas?.dispose?.();
      this.rig = null;
      if (this.sprite) this.sprite.atlas = null;
    },
  };
}

/**
 * Validates an authored `fire` block into a {@link import('../types.js').FireSpec}
 * or `null`. All-or-nothing on purpose: `EnemyManager` branches on
 * `type.fire !== null` to decide melee vs ranged, and a partially-filled spec
 * would make that branch lie. A `null` here just means "melee", which is the
 * safe reading of a broken def.
 *
 * ⚠ THE ALL-OR-NOTHING RULE COVERS THE THREE REQUIRED NUMBERS ONLY.
 * `braceFrac`/`recoverFrac` (the per-weapon override of `EnemyManager#STANCE`'s
 * shoot-from-a-halt duty cycle) are OPTIONAL and default to `null`, read
 * through `??`: an unauthored or nonsensical one falls back to the manager's
 * table, and NEITHER may ever make an otherwise-valid spec return null — a
 * typo'd stance must not silently turn a shooter into a melee unit.
 * ⚠ Nothing in `src/game/enemies/` authors either today, and the two must keep
 * summing to < 1 (see `STANCE`) or that unit never closes.
 *
 * @param {any} f
 * @returns {import('../types.js').FireSpec|null}
 */
function normalizeFire(f) {
  if (!f || typeof f !== 'object') return null;
  const interval = Number(f.interval);
  const range = Number(f.range);
  const damage = Number(f.damage);
  if (!(interval > 0) || !(range > 0) || !(damage > 0)) return null;
  return {
    interval,
    range,
    damage,
    braceFrac: frac01(f.braceFrac),
    recoverFrac: frac01(f.recoverFrac),
  };
}

/**
 * A duty-cycle FRACTION: a finite number in `[0, 1)`, or `null` for "use the
 * default". 1 and up is rejected rather than clamped, because a whole cycle
 * spent planted is a unit that never closes, and falling back to the shipped
 * table is the safe reading of that typo.
 * @param {any} v
 * @returns {number|null}
 */
function frac01(v) {
  if (v === undefined || v === null) return null;
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 && n < 1 ? n : null;
}

/**
 * Atlas ROW for an actor yaw seen from a camera yaw. Four facings, so the
 * quantiser is `round(rel / 90deg)`.
 *
 * Convention: an actor whose yaw EQUALS the camera yaw is walking toward the
 * viewer (the camera sits at `target + (sin yaw, _, cos yaw) * d` and looks back
 * along `-(sin yaw, cos yaw)`), so that is row 0 = FRONT. See
 * {@link FACING_ROWS}.
 *
 * Authored 2D enemies are single-row and override this with a constant — a flat
 * painted creature looks the same from every angle, which is correct for this
 * art style. It stays the default for anything that DOES carry facings.
 *
 * @param {number} yaw Actor heading, radians.
 * @param {number} camYaw Rig yaw, radians.
 * @returns {number} 0..3
 */
export function rowFor(yaw, camYaw) {
  const rel = yaw - camYaw;
  const q = Math.round(rel / (Math.PI / 2));
  return ((q % 4) + 4) % 4;
}

// ─────────────────────────────────────────────────────────────────────────────
// The one placeholder
// ─────────────────────────────────────────────────────────────────────────────

/**
 * `placeholder` — a grey lump with two eyes. No art files, no fetch, no GPU,
 * deterministic from a seed.
 *
 * It exists so a level that ships without authored enemies still spawns
 * something the AI, the spawner, the HUD and both renderers can chew on. It is
 * intentionally dull: anything that looks like a design decision here would get
 * copied, and the whole point of this session was that generated creatures are
 * not the way.
 *
 * @param {Object} [o]
 * @param {string|number} [o.seed='enemies'] Same seed => byte-identical mesh.
 * @param {boolean} [o.sprites=true] Paint the atlas (skipped in node/tests,
 *   where a `DataTexture` stand-in is produced instead).
 * @returns {EnemyType}
 */
export function buildPlaceholderType(o = {}) {
  const { seed = 'enemies', sprites = true } = o;
  const gb = new GeoBuilder({ rng: streamRng(seed, 'placeholder:geo'), tintJitter: 0.05, defaultMat: 'body' });
  const occ = new OccluderSet();
  /** @type {THREE.BufferGeometry[]} */
  const parts = [];

  const GREY = new THREE.Color('#8b8f96');
  const GREY_DEEP = new THREE.Color('#5f646b');
  const EYE = new THREE.Color('#16181c');

  const m = new THREE.Matrix4();
  const q = new THREE.Quaternion();
  const p = new THREE.Vector3();
  const s = new THREE.Vector3();

  /**
   * @param {THREE.BufferGeometry} geo
   * @param {number} x @param {number} y @param {number} z
   * @param {THREE.Color} tint @param {string} [mat]
   */
  const put = (geo, x, y, z, tint, mat = 'body') => {
    m.compose(p.set(x, y, z), q.identity(), s.set(1, 1, 1));
    gb.add(geo, m, { mat, tint });
    geo.computeBoundingBox();
    const bb = /** @type {THREE.Box3} */ (geo.boundingBox);
    occ.addBox(
      { x: x + bb.min.x, y: y + bb.min.y, z: z + bb.min.z },
      { x: x + bb.max.x, y: y + bb.max.y, z: z + bb.max.z },
    );
    parts.push(geo);
  };

  // A base pad so the lump SITS (feet exactly at y = 0), a body, a head.
  put(box(0.5, 0.1, 0.42), 0, 0.05, 0, GREY_DEEP);
  put(icoRock(`${seed}:placeholder:body`, { radius: 0.4, detail: 1, jitter: 0.12, squash: 0.1 }), 0, 0.44, 0, GREY);
  put(icoRock(`${seed}:placeholder:head`, { radius: 0.22, detail: 0, jitter: 0.1 }), 0, 0.92, 0.02, GREY);
  // Eyes: the only feature that reads at 52 degrees of pitch.
  put(box(0.09, 0.09, 0.05), -0.11, 0.96, 0.2, EYE, 'accent');
  put(box(0.09, 0.09, 0.05), 0.11, 0.96, 0.2, EYE, 'accent');

  occ.build({ cell: 0.5 });
  const merged = gb.merge();
  gb.release();

  /** @type {THREE.BufferGeometry[]} */
  const baked = [];
  for (const [, geo] of merged) {
    applyBake(geo, bakeSync(geo, occ, null, CREATURE_BAKE));
    baked.push(geo);
  }
  const geometry = baked.length === 1 ? baked[0] : mergeNonIndexed(baked);
  if (baked.length > 1) for (const g of baked) g.dispose();
  for (const g of parts) g.dispose();
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();

  return defineEnemyType({
    id: 'placeholder',
    name: 'Placeholder',
    radius: 0.42,
    speed: 3.0,
    hp: 30,
    damage: 8,
    // Measured: the lump is 0.8 x 1.14 x 0.8 m. The sprite frame's aspect and
    // the painter's extents match it, so the two halves agree on the size.
    worldHeight: 1.14,
    attackCooldown: 1.1,
    blobR: 0.46,
    // A one-part rig: the placeholder is deliberately the simplest possible
    // client of the rig path, and the headless proof that it stays loadable
    // without a DOM or GPU.
    rig: rigFromGeometry(geometry, { name: 'body', generator: 'placeholder' }),
    sprite: buildPlaceholderSprite({ worldH: 1.14, enabled: sprites }),
  });
}

/**
 * Back-compat alias: levels and dev scenes ask for "the built-ins" and get the
 * single placeholder. Returned as an array because `typeIdx` is positional.
 * @param {{seed?: string|number, sprites?: boolean}} [o]
 * @returns {EnemyType[]}
 */
export function buildBuiltinTypes(o = {}) {
  return [buildPlaceholderType(o)];
}

/**
 * The placeholder's one painted frame: a grey blob with two eyes, drawn to the
 * mesh's measured proportions.
 *
 * @param {{worldH: number, enabled?: boolean}} o
 * @returns {import('../types.js').EnemySpriteBackend}
 */
function buildPlaceholderSprite({ worldH, enabled = true }) {
  const frameW = 40;
  const frameH = 56;
  const atlas = enabled && typeof document !== 'undefined'
    ? paintPlaceholder(frameW, frameH)
    : blankAtlas(frameW, frameH, 'placeholder');

  return {
    atlas,
    rows: ATLAS.rows,
    cols: ATLAS.cols,
    frameW,
    frameH,
    worldH,
    anchorY: 0,
    rowFor: () => 0,
  };
}

/**
 * @param {number} w @param {number} h
 * @returns {THREE.CanvasTexture}
 */
function paintPlaceholder(w, h) {
  const cv = document.createElement('canvas');
  cv.width = w;
  cv.height = h;
  const g = /** @type {CanvasRenderingContext2D} */ (cv.getContext('2d'));
  g.clearRect(0, 0, w, h);

  const base = h - h * 0.02;
  g.save();
  g.translate(w / 2, base);

  g.fillStyle = '#5f646b';
  g.fillRect(-w * 0.31, -h * 0.09, w * 0.62, h * 0.09);
  g.fillStyle = '#8b8f96';
  g.beginPath();
  g.ellipse(0, -h * 0.4, w * 0.35, h * 0.33, 0, 0, TAU);
  g.fill();
  g.beginPath();
  g.ellipse(0, -h * 0.8, w * 0.2, h * 0.19, 0, 0, TAU);
  g.fill();
  g.fillStyle = '#16181c';
  g.fillRect(-w * 0.16, -h * 0.85, w * 0.09, h * 0.07);
  g.fillRect(w * 0.07, -h * 0.85, w * 0.09, h * 0.07);
  g.restore();

  const tex = new THREE.CanvasTexture(cv);
  tex.name = 'enemy:placeholder:atlas';
  configureSpriteTexture(tex);
  tex.minFilter = THREE.LinearFilter;
  tex.generateMipmaps = false;
  tex.needsUpdate = true;
  return tex;
}

/**
 * Node / worker stand-in so this module stays importable without a DOM (the
 * unit tests build the placeholder).
 * @param {number} w @param {number} h @param {string} id
 * @returns {THREE.DataTexture}
 */
function blankAtlas(w, h, id) {
  const tex = new THREE.DataTexture(new Uint8Array(w * h * 4), w, h);
  tex.name = `enemy:${id}:atlas(blank)`;
  tex.generateMipmaps = false;
  tex.needsUpdate = true;
  return tex;
}

/**
 * Frees every GPU resource a type owns. Levels call this in `dispose()` when
 * they built the types themselves (the registry cannot own them: types can
 * outlive a single level).
 * @param {EnemyType[]} types
 */
export function disposeTypes(types) {
  for (const t of types) t?.dispose?.();
}

export { ATLAS as ENEMY_ATLAS };
